import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MessageSquare, Users, Calendar, FileText,
  Package, UserRoundCheck, LineChart, Zap, ShieldCheck, Rocket,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useConsultation } from "../context/ConsultationContext";

const demos = [
  { path: "/demo/receptionist", icon: MessageSquare, title: "AI Receptionist & WhatsApp", tag: "Live AI",
    blurb: "24/7 chatbot on your website and WhatsApp — answers FAQs, books appointments, transfers to a human." },
  { path: "/demo/crm", icon: Users, title: "Lead Capture & CRM", tag: "Kanban",
    blurb: "Capture, qualify and score leads automatically. Drag-and-drop pipeline with deal value in GHS." },
  { path: "/demo/appointments", icon: Calendar, title: "Appointment Booking", tag: "Calendar",
    blurb: "Self-service booking with staff, service, date and time. Auto-confirmations by email & WhatsApp." },
  { path: "/demo/invoicing", icon: FileText, title: "Quotation & Invoice", tag: "Auto PDF",
    blurb: "One-click quotations with VAT & discount. Convert quote → invoice → track payment." },
  { path: "/demo/inventory", icon: Package, title: "Inventory & Sales", tag: "Charts",
    blurb: "Live stock, low-stock alerts, sales history, top products and profit reports for retail." },
  { path: "/demo/hr", icon: UserRoundCheck, title: "HR & Employee Onboarding", tag: "Workflow",
    blurb: "Digital onboarding checklist, staff directory, leave balance and training completion." },
  { path: "/demo/analytics", icon: LineChart, title: "Executive Analytics", tag: "Real-time",
    blurb: "One dashboard for revenue, sales, leads, employees and inventory — export to Excel & PDF." },
];

const Container = ({ children, className = "" }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const HeroImage = () => (
  <div className="relative">
    <div className="absolute -inset-6 bg-primary/10 rounded-3xl blur-2xl" />
    <div className="relative glass-strong rounded-3xl p-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 rounded-xl bg-primary/95 text-primary-foreground p-5">
          <div className="text-xs uppercase tracking-widest opacity-80">Monthly revenue</div>
          <div className="mt-2 text-3xl font-display font-bold">GHS ₵ 84,900</div>
          <div className="mt-1 text-accent text-sm font-semibold flex items-center gap-1">
            ▲ +18.6% vs Dec
          </div>
        </div>
        <div className="rounded-xl bg-background/70 border border-border p-4">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">Leads today</div>
          <div className="mt-1 text-2xl font-display font-bold">27</div>
        </div>
        <div className="rounded-xl bg-background/70 border border-border p-4">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">AI chats</div>
          <div className="mt-1 text-2xl font-display font-bold">412</div>
        </div>
        <div className="col-span-2 rounded-xl bg-background/70 border border-border p-4 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Ama · AI Receptionist</span>
            <span className="inline-flex items-center gap-1 text-accent font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" /> Live
            </span>
          </div>
          <div className="text-sm rounded-lg bg-muted p-2 max-w-[85%]">Hello 👋 how can StaffLink help your business today?</div>
          <div className="text-sm rounded-lg bg-primary text-primary-foreground p-2 max-w-[85%] ml-auto">
            I need help automating my customer service.
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const Landing = () => {
  const { openWith } = useConsultation();

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden hero-grid">
        <div className="absolute inset-0 dot-pattern opacity-40 pointer-events-none" />
        <Container className="relative py-14 lg:py-28 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <Badge className="bg-accent/15 text-accent hover:bg-accent/20 border-none" data-testid="hero-badge">
              <Zap className="w-3 h-3 mr-1" /> Business automation for growing businesses worldwide
            </Badge>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]"
            >
              Business Automation That <span className="brand-gradient-text">Works While You Sleep</span>
            </motion.h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Experience real examples of how StaffLink Ghana helps businesses automate operations,
              improve customer service and increase productivity — from AI receptionists to full executive dashboards.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="h-12 px-6 bg-primary hover:bg-primary/90 text-base" data-testid="explore-demos-btn">
                <a href="#demos">Explore Live Demos <ArrowRight className="ml-2 w-4 h-4" /></a>
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-6 text-base" onClick={() => openWith({})} data-testid="hero-book-consultation">
                Book Free Consultation
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 sm:gap-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-accent" /> Enterprise-grade security</div>
              <div className="flex items-center gap-2"><Rocket className="w-4 h-4 text-accent" /> Live in 2 weeks</div>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <HeroImage />
          </motion.div>
        </Container>
      </section>

      {/* Demo cards */}
      <section id="demos" className="py-20">
        <Container>
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary">Seven live demos</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl">See automation in action.</h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Every card below launches a fully-interactive module with realistic sample data.
              Play with it, break it, then imagine it running your business.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {demos.map((d, i) => {
              const Icon = d.icon;
              return (
                <motion.div
                  key={d.path}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                >
                  <Link
                    to={d.path}
                    data-testid={`demo-card-${i + 1}`}
                    className="group block h-full glass rounded-2xl p-6 border border-border/50 hover:-translate-y-1 hover:shadow-lg transition-transform duration-300 ease-out"
                  >
                    <div className="flex items-center justify-between">
                      <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <Icon className="w-5 h-5" />
                      </div>
                      <Badge variant="secondary" className="text-[10px] uppercase tracking-widest">{d.tag}</Badge>
                    </div>
                    <div className="mt-5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Demo {i + 1}</div>
                    <h3 className="mt-1 font-display text-xl font-semibold">{d.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d.blurb}</p>
                    <div className="mt-5 inline-flex items-center text-sm font-semibold text-primary">
                      Open demo <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="pb-24">
        <Container>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-[#00427F] text-primary-foreground p-10 md:p-14">
            <div className="absolute -top-20 -right-10 w-72 h-72 bg-accent/25 blur-3xl rounded-full" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-sm uppercase tracking-widest text-primary-foreground/80">Ready to automate?</p>
                <h2 className="mt-2 font-display text-3xl md:text-4xl leading-tight">
                  Let's turn one of these demos into your business reality.
                </h2>
                <p className="mt-3 text-primary-foreground/80 max-w-lg">
                  Free 30-minute strategy call. We'll map out exactly which automations will move the needle for you.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-primary hover:bg-white/90 h-12 px-6 text-base font-semibold"
                  onClick={() => openWith({})}
                  data-testid="landing-final-consultation"
                >
                  Book Free Consultation <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Landing;
